import { Link } from 'react-router-dom';
import clsx from 'clsx';

/**
 * Page hero — dark slate background with accent-tinted gradient band.
 */
export function Hero({ eyebrow, title, description, primaryCta, secondaryCta, children }) {
  return (
    <section className="relative overflow-hidden bg-slate-950 text-white">
      <div
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            'radial-gradient(1200px circle at 10% -20%, rgba(56,189,248,0.25), transparent 40%), radial-gradient(900px circle at 85% 120%, rgba(14,165,233,0.18), transparent 45%)',
        }}
        aria-hidden
      />
      <div className="relative container-wide py-20 md:py-28">
        {eyebrow && (
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-sky-300">
            {eyebrow}
          </div>
        )}
        <h1 className="max-w-4xl text-4xl font-semibold leading-[1.1] tracking-tight text-white md:text-5xl lg:text-6xl">
          {title}
        </h1>
        {description && (
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
            {description}
          </p>
        )}
        {(primaryCta || secondaryCta) && (
          <div className="mt-10 flex flex-wrap items-center gap-3">
            {primaryCta && (
              <Link to={primaryCta.to} className="btn-primary">
                {primaryCta.label}
              </Link>
            )}
            {secondaryCta && (
              <Link
                to={secondaryCta.to}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white transition hover:border-white/25 hover:bg-white/10"
              >
                {secondaryCta.label}
              </Link>
            )}
          </div>
        )}
        {children && <div className="relative mt-14">{children}</div>}
      </div>
    </section>
  );
}

export function SectionHeader({ eyebrow, title, description, align = 'left' }) {
  return (
    <div className={clsx('max-w-3xl', align === 'center' && 'mx-auto text-center')}>
      {eyebrow && (
        <div className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-sky-600">
          {eyebrow}
        </div>
      )}
      <h2 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
        {title}
      </h2>
      {description && (
        <p className="mt-4 text-base leading-7 text-slate-600 md:text-lg md:leading-8">
          {description}
        </p>
      )}
    </div>
  );
}

/**
 * 2- or 3-column feature grid. `items` = [{icon: Component, title, description, to?}].
 */
export function FeatureGrid({ items, columns = 3 }) {
  return (
    <div
      className={clsx(
        'grid gap-6 md:gap-8',
        columns === 2 && 'md:grid-cols-2',
        columns === 3 && 'md:grid-cols-2 lg:grid-cols-3',
        columns === 4 && 'md:grid-cols-2 lg:grid-cols-4',
      )}
    >
      {items.map((item, i) => {
        const Icon = item.icon;
        const inner = (
          <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 transition hover:border-slate-300 hover:shadow-sm">
            {Icon && (
              <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-700">
                <Icon className="h-5 w-5" strokeWidth={2} />
              </div>
            )}
            <h3 className="text-base font-semibold text-slate-900">{item.title}</h3>
            <p className="mt-2 flex-1 text-sm leading-6 text-slate-600">{item.description}</p>
            {item.to && (
              <div className="mt-4 text-sm font-semibold text-sky-600">Learn more →</div>
            )}
          </div>
        );
        return item.to ? (
          <Link key={i} to={item.to} className="block">
            {inner}
          </Link>
        ) : (
          <div key={i}>{inner}</div>
        );
      })}
    </div>
  );
}

/**
 * Full-bleed call-to-action band with optional secondary link.
 */
export function CTABanner({ title, description, primaryCta, secondaryCta }) {
  return (
    <section className="bg-slate-900">
      <div className="container-wide py-16 md:py-20">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">
              {title}
            </h2>
            {description && (
              <p className="mt-3 text-base leading-7 text-slate-300">{description}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {primaryCta && (
              <Link to={primaryCta.to} className="btn-primary">
                {primaryCta.label}
              </Link>
            )}
            {secondaryCta && (
              <Link
                to={secondaryCta.to}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white transition hover:border-white/25 hover:bg-white/10"
              >
                {secondaryCta.label}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Alternating content / visual slab for subsystem + audience pages.
 */
export function Slab({ eyebrow, title, description, bullets, image, reverse = false, actions }) {
  return (
    <section className="py-16 md:py-20">
      <div className="container-wide">
        <div className={clsx('grid items-center gap-10 lg:grid-cols-2 lg:gap-16', reverse && 'lg:[&>*:first-child]:order-2')}>
          <div>
            {eyebrow && (
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-sky-600">
                {eyebrow}
              </div>
            )}
            <h2 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
              {title}
            </h2>
            {description && (
              <p className="mt-4 text-base leading-7 text-slate-600 md:text-lg md:leading-8">
                {description}
              </p>
            )}
            {bullets && (
              <ul className="mt-6 space-y-3">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700">
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" aria-hidden />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {actions && <div className="mt-8 flex flex-wrap gap-3">{actions}</div>}
          </div>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
            {image}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Decorative placeholder visual for slabs (no external asset dependencies).
 * Renders a stylised dashboard mock using plain SVG + tailwind.
 */
export function MockVisual({ accent = 'sky', lines = 5 }) {
  const accentCls =
    accent === 'emerald' ? 'bg-emerald-500' :
    accent === 'amber'   ? 'bg-amber-500'   :
    accent === 'rose'    ? 'bg-rose-500'    :
    'bg-sky-500';

  return (
    <div className="aspect-[4/3] w-full bg-gradient-to-br from-slate-100 to-white p-6">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-rose-400" />
        <span className="h-2 w-2 rounded-full bg-amber-400" />
        <span className="h-2 w-2 rounded-full bg-emerald-400" />
      </div>
      <div className="mt-6 space-y-3">
        <div className={clsx('h-2 w-28 rounded-full', accentCls)} />
        <div className="h-3 w-48 rounded-full bg-slate-300" />
        <div className="h-3 w-72 rounded-full bg-slate-200" />
      </div>
      <div className="mt-8 grid grid-cols-3 gap-3">
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className="h-16 rounded-lg bg-white shadow-sm ring-1 ring-slate-200">
            <div className={clsx('m-2 h-1.5 w-8 rounded-full', accentCls)} />
            <div className="m-2 h-2 w-14 rounded-full bg-slate-200" />
            <div className="m-2 h-2 w-10 rounded-full bg-slate-200" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function StatBlock({ stats }) {
  return (
    <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
      {stats.map((s, i) => (
        <div key={i} className="border-l-2 border-sky-500 pl-4">
          <div className="text-3xl font-semibold tracking-tight text-slate-900">{s.value}</div>
          <div className="mt-1 text-sm text-slate-600">{s.label}</div>
        </div>
      ))}
    </div>
  );
}
